// Packages
import * as Sentry from '@sentry/vue'
import axios from 'axios'
import { ExtraErrorData } from '@sentry/integrations'
import Vue from 'vue'
// Helpers
import { CWarn, CError } from './consoleMixin'

const disabledEnvironments = [
  'demo',
  'dev',
  'devcloud',
  'docker',
  'prod',
  'prodlocal',
  // 'production',
  'test',
]
const environment = process.env.VUE_APP_ENV

/*
 for tracking a generic event
 label is the event name
 data is some data to attach to it
 can use this for passing state along with an exception
 level can be fatal, error, warning, info, and debug
*/
export const cancelTransactionInProgress = ($store, vuexTransactionName) => {
  if ($store.getters.sentryTransactions[vuexTransactionName]) {
    const transaction = $store.getters.sentryTransactions[vuexTransactionName]
    if (transaction) {
      $store.commit('STORE_SENTRY_TRANSACTION', {
        transaction: null,
        vuexTransactionName,
      })
    }
  }
}

export const captureSentryEvent = (label, state, level = 'error') => {
  if (!disabledEnvironments.includes(environment)) {
    // we want to avoid logging JWT token expiration/malformed errors
    const matchPattern = new RegExp(/TokenExpiredError|JsonWebTokenError|AuthError|401|Redirected\s*when\s*going\s*from|Navigation\s*cancelled|api\/announcements/)
    const code = state?.response?.data?.code ?? state?.response?.data?.statusCode
    const matchedCode = code?.toString().match(matchPattern)
    const matchedMessage = state?.response?.data?.message?.match(matchPattern)
    const errorCode = state?.response?.data?.error?.code ?? state?.response?.data?.error?.statusCode
    const matchedErrorCode = errorCode?.toString().match(matchPattern)
    const matchedDetails = state?.details?.message?.match(matchPattern)
    if (
      matchedMessage ||
      matchedCode ||
      matchedErrorCode ||
      matchedDetails
    ) {
      return
    }
    Sentry.configureScope((scope) => {
      scope.setExtra('state', state)
      scope.setLevel(level)
      Sentry.captureEvent({
        message: label,
      })
    })
  }
  else {
    CWarn('SENTRY IS NOT ENABLED FOR ENV:', environment)
  }
}

export const captureSentryTransaction = (sentryTransactionName, opName, tags = {}) => {
  if (!disabledEnvironments.includes(environment)) {
    return Sentry.startTransaction({
      // Human-readable identifier, like "GET batch-report"
      name: sentryTransactionName,
      // Short description of transaction type, such as "request"
      op: opName,
      // Guarantees being sent to Sentry (ignores sample rate)
      sampled: true,
      tags,
    })
  }
  else {
    CWarn('SENTRY IS NOT ENABLED FOR ENV:', environment)
  }
}

export const finishTransactionInProgress = ($store, vuexTransactionName, additionalTags = {}) => {
  if ($store.getters.sentryTransactions[vuexTransactionName]) {
    const transaction = $store.getters.sentryTransactions[vuexTransactionName]

    if (transaction) {
      transaction.tags = { ...transaction.tags, ...additionalTags }
      transaction.finish()
      $store.commit('STORE_SENTRY_TRANSACTION', {
        transaction: null,
        vuexTransactionName,
      })
    }
  }
}

export const initializeSentry = async () => {
  const version = process.env.VUE_APP_GIT_RELEASE_VERSION
  if (!disabledEnvironments.includes(environment)) {
    let metadata = {}
    try {
      metadata = (await axios.get('https://json.geoiplookup.io/', {
        timeout: 500,
      })).data
    }
    catch (err) {
      CError('Error loading metadata:', err)
    }
    const user = JSON.parse(localStorage.getItem('user')) || {}
    // This will allow us to search for issues based on type of user
    Sentry.setTag('isClient', !!user.client)
    Sentry.setTag('isEmployee', !!user.employee)
    Sentry.setTag('isMaster', !!user.employee && !!user.employee.is_master)
    Sentry.setTag('metadata', metadata)
    Sentry.init({
      async beforeSend (event) {
        // You can only attach properties that Sentry is expecting. If you need something special
        // and want to track it, add it as a tag above
        event.user = user
        return event
      },
      denyUrls: [
        '/maintenance',
      ],
      dsn: process.env.VUE_APP_SENTRY_CLIENT_DSN,
      environment,
      // ExtraErrorData allows deeper level objects in additional data state
      ignoreErrors: [
        'Error: Navigation cancelled from',
      ],
      integrations: [
        new ExtraErrorData({ depth: 7 }),
        new Sentry.BrowserTracing(),
        new Sentry.Replay({
          networkDetailAllowUrls: [ '' ],
          networkRequestHeaders: [ 'X-Custom-Header' ],
          networkResponseHeaders: [ 'X-Custom-Header' ],
        }),
      ],
      normalizeDepth: 8, // ExtraErrorData depth + 1
      release: version,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 0.5,
      tracesSampleRate: 0.1,
      Vue,
    })
  }
  else {
    CWarn('SENTRY IS NOT ENABLED FOR ENV:', environment)
  }
}

export const startTransaction = (
  $store,
  vuexTransactionName,
  sentryTransactionName,
  sentryOpName,
  tags = {}
) => {
  $store.commit('STORE_SENTRY_TRANSACTION', {
    transaction: captureSentryTransaction(sentryTransactionName, sentryOpName, tags),
    transactionName: vuexTransactionName,
  })
}

export default {
  methods: {
    cancelTransactionInProgress,
    captureSentryEvent,
    captureSentryTransaction,
    finishTransactionInProgress,
    initializeSentry,
    startTransaction,
  },
}
